import React, { useState, useEffect, useRef } from "react";
import ReactMapGL, {
  Popup,
  NavigationControl,
  FullscreenControl,
  ScaleControl,
  GeolocateControl,
  Source,
  Layer,
} from "react-map-gl";
import { Chip } from "@material-ui/core";

const MAPBOX_TOKEN =
  "pk.eyJ1IjoieWFtYXNuYXgiLCJhIjoiY2prOTlub3cwMnQ5dzNsbWxpNXpxdTJoNyJ9.iPwzkcOPb0xHua5IwXCElA";
//  "pk.eyJ1IjoieWFtYXNuYXgiLCJhIjoiY2x1YmFuaWVnMHVnMDJzbGQwdzNqYjk5biJ9.Oie3_d5us7a0b1e5abksgg";
// pk.eyJ1IjoieWFtYXNuYXgiLCJhIjoiY2x1YmFuaWVnMHVnMDJzbGQwdzNqYjk5biJ9.Oie3_d5us7a0b1e5abksgg // KpiDash-Dev
// pk.eyJ1IjoicGVsbW9yZXhwZHNnaXMiLCJhIjoiY2swY3Q4anhkMDB1YzNjcWVzdWpxM3JqMyJ9.dXzNM9VAn2gpqRphAGoLTw
// pk.eyJ1IjoiZGVhLXl5YW1hZ3VjaGkiLCJhIjoiY2x1YWo2Y3dyMG5hdjJwdDU2ODA4MHNoYiJ9.q60AWM7wayOHd-LFxbDzDg
// pk.eyJ1IjoiZGVhLXl5YW1hZ3VjaGkiLCJhIjoiY2x1YWpieTRjMGxkYzJrazRrdDZobDJtdyJ9.t4Yp-iU5neijrsiope7Onw // KpiDash

const COLOR_TAKEN = "#6e54a6"; // green
const COLOR_UNTAKEN = "#f53b78"; // blue

function PhotoShootMap({ records, mapType, width, height }) {
  const [mapStyle, setMapStyle] = useState(
    "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb"
  );
  const [terrainEnabled, setTerrainEnabled] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState(null);
  const [showClusters, setShowClusters] = useState(false);
  const popupHovered = useRef(false);

  const markerColor = mapType === "taken" ? COLOR_TAKEN : COLOR_UNTAKEN;

  const calculateBounds = (records) => {
    const bounds = records.reduce(
      (acc, curr) => {
        return {
          minLng: Math.min(acc.minLng, parseFloat(curr.longitude)),
          minLat: Math.min(acc.minLat, parseFloat(curr.latitude)),
          maxLng: Math.max(acc.maxLng, parseFloat(curr.longitude)),
          maxLat: Math.max(acc.maxLat, parseFloat(curr.latitude)),
        };
      },
      {
        minLng: Infinity,
        minLat: Infinity,
        maxLng: -Infinity,
        maxLat: -Infinity,
      }
    );

    const paddingPercentage = 0.1;
    const lngPadding = (bounds.maxLng - bounds.minLng) * paddingPercentage;
    const latPadding = (bounds.maxLat - bounds.minLat) * paddingPercentage;

    return {
      minLng: bounds.minLng - lngPadding,
      minLat: bounds.minLat - latPadding,
      maxLng: bounds.maxLng + lngPadding,
      maxLat: bounds.maxLat + latPadding,
    };
  };

  const calculateCenterAndZoom = (bounds) => {
    const center = [
      (bounds.minLng + bounds.maxLng) / 2,
      (bounds.minLat + bounds.maxLat) / 2,
    ];
    const zoom = Math.min(
      Math.log2(960 / (bounds.maxLng - bounds.minLng)),
      Math.log2(480 / (bounds.maxLat - bounds.minLat))
    );
    return { center, zoom };
  };

  const boundsWithPadding = records.reduce(
    (acc, curr) => {
      return {
        minLng: Math.min(acc.minLng, parseFloat(curr.longitude)),
        minLat: Math.min(acc.minLat, parseFloat(curr.latitude)),
        maxLng: Math.max(acc.maxLng, parseFloat(curr.longitude)),
        maxLat: Math.max(acc.maxLat, parseFloat(curr.latitude)),
      };
    },
    {
      minLng: Infinity,
      minLat: Infinity,
      maxLng: -Infinity,
      maxLat: -Infinity,
    }
  );
  const paddingPercentage = 0.1;

  const lngPadding =
    (boundsWithPadding.maxLng - boundsWithPadding.minLng) * paddingPercentage;
  const latPadding =
    (boundsWithPadding.maxLat - boundsWithPadding.minLat) * paddingPercentage;
  const bounds = {
    minLng: boundsWithPadding.minLng - lngPadding,
    minLat: boundsWithPadding.minLat - latPadding,
    maxLng: boundsWithPadding.maxLng + lngPadding,
    maxLat: boundsWithPadding.maxLat + latPadding,
  };

  const center = [
    (bounds.minLng + bounds.maxLng) / 2,
    (bounds.minLat + bounds.maxLat) / 2,
  ];

  const zoom = Math.min(
    Math.log2(960 / (bounds.maxLng - bounds.minLng)),
    Math.log2(480 / (bounds.maxLat - bounds.minLat))
  );

  const [viewport, setViewport] = React.useState({
    latitude: center[1], //avgLatitude, // Maebashi 36.393351
    longitude: center[0], //avgLongitude, // Maebashi 139.064853
    zoom: zoom,
    bearing: terrainEnabled ? 80 : 0,
    pitch: terrainEnabled ? 80 : 0,
  });

  useEffect(() => {
    if (records.length > 0) {
      const bounds = calculateBounds(records);
      const { center, zoom } = calculateCenterAndZoom(bounds);
      setViewport({
        latitude: center[1],
        longitude: center[0],
        zoom: zoom,
        bearing: terrainEnabled ? 80 : 0,
        pitch: terrainEnabled ? 80 : 0,
      });
    }
  }, [records, terrainEnabled]);

  const toggleMapStyle = () => {
    setMapStyle((prev) =>
      prev === "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb"
        ? "mapbox://styles/mapbox/satellite-v9"
        : "mapbox://styles/yamasnax/clubgryio000s01p98ef99wcb"
    );
  };

  const toggleTerrain = () => {
    setTerrainEnabled(!terrainEnabled);
  };

  const toGeoJSON = (rows) => ({
    type: "FeatureCollection",
    features: (rows || []).map((r) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [parseFloat(r.longitude), parseFloat(r.latitude)],
      },
      properties: {
        id: r.id,
        power_asset_id: r.power_asset_id,
        sign_number: r.sign_number,
        sign_name: r.sign_name,
        place_code: r.place_code,
        __kind: r.__kind, // ← 親で付与（taken / untaken）
      },
    })),
  });

  const isBoth = mapType === "taken+untaken";
  const takenRows = isBoth
    ? records.filter((r) => r.__kind === "taken")
    : records;
  const untakenRows = isBoth
    ? records.filter((r) => r.__kind === "untaken")
    : [];
  const gjTaken = toGeoJSON(takenRows);
  const gjUntaken = toGeoJSON(untakenRows);

  const totalTaken = takenRows.length;
  const totalUntaken = untakenRows.length;
  const totalAll = isBoth ? totalTaken + totalUntaken : records.length;

  const gjBoth = {
    type: "FeatureCollection",
    features: (records || []).map((r) => ({
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [parseFloat(r.longitude), parseFloat(r.latitude)],
      },
      properties: {
        id: r.id,
        power_asset_id: r.power_asset_id,
        sign_number: r.sign_number,
        sign_name: r.sign_name,
        place_code: r.place_code,
        __kind: r.__kind, // "taken" | "untaken"
      },
    })),
  };

  const interactiveLayerIds = isBoth
    ? showClusters
      ? ["unclustered-points-both-taken", "unclustered-points-both-untaken"]
      : [
          "unclustered-points-off-both-taken",
          "unclustered-points-off-both-untaken",
        ]
    : showClusters
    ? ["unclustered-points"]
    : ["unclustered-points-off"];

  return (
    <div style={{ width: width, height: height }}>
      <ReactMapGL
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: "100%", height: "90%" }}
        mapStyle={mapStyle}
        {...viewport}
        onMove={(evt) => setViewport(evt.viewState)}
        onMouseMove={(event) => {
          if (popupHovered.current) return;
          const feature = event.features && event.features[0];
          setHoveredFeature(feature);
        }}
        interactiveLayerIds={interactiveLayerIds}
        terrain={
          terrainEnabled ? { source: "mapbox-dem", exaggeration: 1.5 } : null
        }
      >
        <FullscreenControl />
        <NavigationControl />

        {/* ====== 単独表示（taken または untaken） ====== */}
        {!isBoth && (
          <>
            <Source
              id="clustered-data"
              type="geojson"
              data={gjTaken}
              cluster={true}
              clusterMaxZoom={14}
              clusterRadius={50}
              generateId={true}
            >
              {showClusters && (
                <>
                  <Layer
                    id="clusters"
                    type="circle"
                    source="clustered-data"
                    filter={["has", "point_count"]}
                    paint={{
                      "circle-color": "#3E3E3E",
                      "circle-radius": [
                        "step",
                        ["get", "point_count"],
                        32,
                        100,
                        35,
                        750,
                        45,
                      ],
                      "circle-opacity": 0.9,
                    }}
                  />
                  <Layer
                    id="unclustered-points"
                    type="circle"
                    source="clustered-data"
                    filter={["!", ["has", "point_count"]]}
                    paint={{
                      "circle-color": markerColor,
                      "circle-radius": 3,
                      "circle-stroke-width": 2,
                      "circle-stroke-color": "#ffffff",
                    }}
                  />
                  <Layer
                    id="cluster-count-single"
                    type="symbol"
                    source="clustered-data"
                    filter={["has", "point_count"]}
                    layout={{
                      "text-field": [
                        "format",
                        ["get","point_count"], { "font-scale": 1.5 }, "\n", {},
                        ["concat",
                          ["to-string", ["round", ["*", ["/", ["get","point_count"], (records.length || 1)], 100]]],
                          "%"
                        ],
                        { "font-scale": 1.0 }
                      ],
                      "text-size": 12,
                      "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                      "text-anchor": "center",
                      "text-allow-overlap": true,
                    }}
                    paint={{ "text-color": "#ffffff" }}
                  />
                </>
              )}
            </Source>

            {!showClusters && (
              <Source
                id="unclustered-data"
                type="geojson"
                data={gjTaken}
                cluster={false}
                generateId={true}
              >
                <Layer
                  id="unclustered-points-off"
                  type="circle"
                  source="unclustered-data"
                  paint={{
                    "circle-color": markerColor,
                    "circle-radius": 3,
                    "circle-stroke-width": 0,
                    "circle-stroke-color": "#ffffff",
                  }}
                />
              </Source>
            )}
          </>
        )}

        {/* ====== 両方表示（単一Source+clusterProperties） ====== */}
        {isBoth && (
          <Source
            id="clustered-both"
            type="geojson"
            data={gjBoth}
            cluster={true}
            clusterMaxZoom={14}
            clusterRadius={50}
            generateId={true}
            clusterProperties={{
              taken_count:   ["+", ["case", ["==", ["get","__kind"], "taken"],   1, 0]],
              untaken_count: ["+", ["case", ["==", ["get","__kind"], "untaken"], 1, 0]],
            }}
          >
            {showClusters && (
              <>
                <Layer
                  id="clusters-both"
                  type="circle"
                  source="clustered-both"
                  filter={["has","point_count"]}
                  paint={{
                    "circle-color": "#3E3E3E",
                     "circle-radius": [
                      "step",
                      ["get", "point_count"],
                      40,   // ← 旧: 32
                      100, 50,   // ← 旧: 35
                      750, 65    // ← 旧: 45
                    ],
                    "circle-opacity": 0.9,
                  }}
                />

                {/* ← ここを差し替え：% はクラスタ内合計で割る */}
                <Layer
                  id="cluster-count-both"
                  type="symbol"
                  source="clustered-both"
                  filter={["has","point_count"]}
                  layout={{
                    "text-size": 12,
                    "text-font": ["Open Sans Bold","Arial Unicode MS Bold"],
                    "text-anchor": "center",
                    "text-allow-overlap": true,
                    "text-field": [
                      "format",
                      // 済の行
                      "済：", {}, ["get","taken_count"], {}, "（", {},
                      ["concat",
                        ["to-string", [
                          "round",
                          ["*",
                            ["/",
                              ["get","taken_count"],
                              ["max", 1, ["+", ["get","taken_count"], ["get","untaken_count"]]]
                            ],
                            100
                          ]
                        ]],
                        "%"
                      ], {},
                      "）\n", {},
                      // 未の行
                      "未：", {}, ["get","untaken_count"], {}, "（", {},
                      ["concat",
                        ["to-string", [
                          "round",
                          ["*",
                            ["/",
                              ["get","untaken_count"],
                              ["max", 1, ["+", ["get","taken_count"], ["get","untaken_count"]]]
                            ],
                            100
                          ]
                        ]],
                        "%"
                      ], {},
                      "）", {}
                    ],
                  }}
                  paint={{ "text-color": "#ffffff" }}
                />

                {/* 分解後の個別点（色分け） */}
                <Layer
                  id="unclustered-points-both-taken"
                  type="circle"
                  source="clustered-both"
                  filter={["all", ["!", ["has","point_count"]], ["==", ["get","__kind"], "taken"]]}
                  paint={{ "circle-color": COLOR_TAKEN, "circle-radius": 3, "circle-stroke-width": 2, "circle-stroke-color": "#ffffff" }}
                />
                <Layer
                  id="unclustered-points-both-untaken"
                  type="circle"
                  source="clustered-both"
                  filter={["all", ["!", ["has","point_count"]], ["==", ["get","__kind"], "untaken"]]}
                  paint={{ "circle-color": COLOR_UNTAKEN, "circle-radius": 3, "circle-stroke-width": 2, "circle-stroke-color": "#ffffff" }}
                />
              </>
            )}
          </Source>
        )}

        {isBoth && !showClusters && (
          <Source id="unclustered-both" type="geojson" data={gjBoth} cluster={false} generateId={true}>
            <Layer
              id="unclustered-points-off-both-taken"
              type="circle"
              source="unclustered-both"
              filter={["==", ["get","__kind"], "taken"]}
              paint={{ "circle-color": COLOR_TAKEN, "circle-radius": 3, "circle-stroke-width": 0, "circle-stroke-color": "#ffffff" }}
            />
            <Layer
              id="unclustered-points-off-both-untaken"
              type="circle"
              source="unclustered-both"
              filter={["==", ["get","__kind"], "untaken"]}
              paint={{ "circle-color": COLOR_UNTAKEN, "circle-radius": 3, "circle-stroke-width": 0, "circle-stroke-color": "#ffffff" }}
            />
          </Source>
        )}

        {/* ホバー時のツールチップ */}
        {hoveredFeature && (
          <Popup
            longitude={hoveredFeature.geometry.coordinates[0]}
            latitude={hoveredFeature.geometry.coordinates[1]}
            closeButton={false}
            closeOnClick={false}
            anchor="top"
            style={{
              backgroundColor: "white",
              padding: "0px",
              borderRadius: "8px",
              boxShadow: "0px 0px 10px rgba(0,0,0,0.2)",
            }}
            onMouseEnter={() => {
              popupHovered.current = true;
            }}
            onMouseLeave={() => {
              popupHovered.current = false;
              setTimeout(() => {
                if (!popupHovered.current) {
                  setHoveredFeature(null);
                }
              }, 300);
            }}
          >
            <div style={{ fontSize: "12px", fontWeight: "bold" }}>
              📍 {hoveredFeature.properties.prefectural_area}
            </div>
            <div>
              ID:{" "}
              {hoveredFeature.properties.id !== "null"
                ? hoveredFeature.properties.id
                : "--"}
            </div>
            <div>標識番号: {hoveredFeature.properties.sign_number}</div>
            <div>標識名: {hoveredFeature.properties.sign_name}</div>
            <div>スキーマ番号: {hoveredFeature.properties.place_code}</div>

            {/* Google Street View ボタン */}
            <button
              style={{
                marginTop: "10px",
                padding: "5px 10px",
                backgroundColor: "#4285F4",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
              onClick={(e) => {
                e.stopPropagation();
                const lat = hoveredFeature.geometry.coordinates[1];
                const lng = hoveredFeature.geometry.coordinates[0];
                const googleStreetViewUrl = `https://www.google.com/maps?q=&layer=c&cbll=${lat},${lng}`;
                window.open(googleStreetViewUrl, "_blank");
              }}
              onMouseEnter={() => (popupHovered.current = true)} // ボタンの上でも消えない
              onMouseLeave={() => {
                popupHovered.current = false;
                setTimeout(() => {
                  if (!popupHovered.current) {
                    setHoveredFeature(null);
                  }
                }, 300);
              }}
            >
              🏠 Google Street View で見る
            </button>
          </Popup>
        )}

        <div style={{ position: "absolute", top: 10, left: 10, zIndex: 1 }}>
          <Chip
            size="small"
            onClick={toggleMapStyle}
            label={
              mapStyle.includes("yamasnax") ? "航空写真表示" : "デフォルト表示"
            }
            style={{ backgroundColor: "#4caf50", color: "#fff" }}
          />
          <Chip
            size="small"
            onClick={toggleTerrain}
            label={terrainEnabled ? "2D表示" : "3D表示"}
            style={{ backgroundColor: "#4caf50", color: "#fff" }}
          />
          <Chip
            size="small"
            onClick={() => setShowClusters(!showClusters)}
            label={showClusters ? "クラスター表示" : "クラスター非表示"}
            style={{
              backgroundColor: showClusters ? "#4caf50" : "#7f898b",
              color: "#fff",
            }}
          />
        </div>

        {terrainEnabled && (
          <>
            <Source
              id="mapbox-dem"
              type="raster-dem"
              url="mapbox://mapbox.mapbox-terrain-dem-v1"
              tileSize={512}
              maxzoom={14}
            />
            <Layer
              id="terrain-data"
              type="hillshade"
              source="mapbox-dem"
              paint={{
                "hillshade-exaggeration": 0.5,
              }}
            />
          </>
        )}
      </ReactMapGL>
    </div>
  );
}

export default PhotoShootMap;
